'use client';

import Link from 'next/link';
import { Reveal } from '@/components/products/Reveal';
import { ownedProducts, publishedApps } from '@/lib/corporate-content';

/**
 * The owned-IP section: three platforms the company builds, runs and sells,
 * each with a live URL a reviewer can open, plus the two published mobile
 * apps that can be checked in the stores.
 *
 * Layout family: full-bleed stacked bands with a per-product accent rule —
 * distinct from the hairline list above it and the stats grid below.
 */
export function OwnedProductsSection() {
  return (
    <section
      className="self-styled relative z-20 border-b border-white/[0.07]"
      aria-labelledby="products-heading"
    >
      <div className="content-padding">
        <div className="pb-4 pt-24 md:pt-32">
          <Reveal>
            <span className="eyebrow">Owned software</span>
            <h2
              id="products-heading"
              className="font-six-caps mt-6 max-w-5xl uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.75rem, 6.5vw, 6rem)' }}
            >
              Software we build and operate
            </h2>
            <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
              DilaCRM, DilaERP and dila.menu are active products with their own
              release cycles. Use the links below to see each one.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Product bands */}
      <div className="mt-12 md:mt-16">
        {ownedProducts.map((product, i) => (
          <Reveal key={product.name} delay={i * 60}>
            <article
              className="relative border-t border-white/10"
              style={{ backgroundColor: i % 2 === 0 ? 'rgba(10,10,10,0.5)' : 'transparent' }}
            >
              {/* Accent rule — the only place the per-product colour appears here */}
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 h-px w-16 md:w-28"
                style={{ backgroundColor: product.accent }}
              />
              <div className="content-padding">
                <div className="grid grid-cols-1 gap-8 py-12 md:py-16 lg:grid-cols-[5rem_1fr_1fr] lg:gap-12">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/35">
                    {product.index}
                  </span>

                  <div className="flex flex-col gap-5">
                    <h3
                      className="text-2xl font-light leading-tight text-white md:text-[2rem]"
                      style={{ letterSpacing: '-0.01em' }}
                    >
                      {product.name}
                    </h3>
                    <p className="max-w-xl text-sm font-light leading-relaxed text-white/60 md:text-base">
                      {product.summary}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-1">
                      {product.href && (
                        <Link
                          href={product.href}
                          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-opacity hover:opacity-70"
                          style={{ color: product.accent }}
                        >
                          Product detail
                          <span
                            aria-hidden="true"
                            className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                          >
                            &rarr;
                          </span>
                        </Link>
                      )}
                      <a
                        href={product.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 transition-colors hover:text-white"
                      >
                        {product.liveLabel}
                        <span aria-hidden="true">&#8599;</span>
                      </a>
                    </div>
                  </div>

                  <ul className="flex flex-col gap-3 lg:pt-2">
                    {product.facts.map((fact) => (
                      <li key={fact} className="flex gap-3">
                        <span
                          aria-hidden="true"
                          className="mt-[0.6em] h-px w-3 flex-shrink-0"
                          style={{ backgroundColor: product.accent, opacity: 0.75 }}
                        />
                        <span className="text-sm font-light leading-relaxed text-white/55">
                          {fact}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      {/* Published apps — a short, checkable coda rather than a fourth band */}
      <div className="content-padding border-t border-white/10">
        <div className="grid grid-cols-1 gap-8 py-14 md:grid-cols-[minmax(0,16rem)_1fr] md:gap-16 md:py-16">
          <Reveal>
            <span className="eyebrow">Also published</span>
            <p className="mt-5 max-w-xs text-sm font-light leading-relaxed text-white/50">
              Consumer apps from the same team, live on the Apple App Store and
              Google Play.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-px sm:grid-cols-2" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
            {publishedApps.map((app, i) => (
              <Reveal key={app.name} delay={i * 70}>
                <Link
                  href={app.href}
                  className="group flex h-full flex-col gap-3 p-6 transition-colors md:p-8"
                  style={{ backgroundColor: '#0a0a0a' }}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/35">
                    {app.detail}
                  </span>
                  <span className="text-lg font-light text-white transition-colors group-hover:text-[var(--color-accent)]">
                    {app.name}
                  </span>
                  <span className="text-sm font-light leading-relaxed text-white/50">
                    {app.summary}
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
